import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Gavel, Search, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getApiUrl } from "@/utils/api";
import PlanningAppealDetailModal from "@/components/shared/PlanningAppealDetailModal";

const API = getApiUrl();

const DECISION_STYLE = {
  Allowed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Dismissed: "bg-rose-100 text-rose-800 border-rose-200",
  "Split Decision": "bg-amber-100 text-amber-800 border-amber-200",
  Withdrawn: "bg-muted text-muted-foreground border-border",
};

function formatDMY(isoDate) {
  if (!isoDate) return "-";
  const parts = String(isoDate).slice(0, 10).split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

/**
 * Agent dashboard -> "Planning Appeals" section. Searches our own mirror
 * of the Planning Inspectorate Casework Database (refreshed weekly) by
 * postcode district - handy for checking if a nearby refusal was
 * overturned on appeal.
 */
export default function PlanningAppealsTool({ publicMode = false }) {
  const [postcode, setPostcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedAppeal, setSelectedAppeal] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pc = postcode.trim();
    if (!pc) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/${publicMode ? "public" : "property-admin"}/planning-appeals/search`, {
        params: { postcode: pc },
        withCredentials: !publicMode,
      });
      setResult(data);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Failed to search planning appeals";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="planning-appeals-tool">
      <div className="bg-white border border-border p-6">
        <h3 className="font-serif text-xl font-medium flex items-center gap-2">
          <Gavel className="h-5 w-5 text-emerald-600" />
          Planning Appeals
        </h3>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          Search the Planning Inspectorate's appeal decisions by postcode or by street/house name - useful for
          checking if a refused application nearby was later allowed on appeal.
        </p>

        <form onSubmit={handleSubmit} className="flex items-center gap-2 max-w-md">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g. EN11 0RD or Stow Park Drive"
              className="pl-9 rounded-none"
              data-testid="planning-appeals-tool-postcode-input"
            />
          </div>
          <Button type="submit" disabled={loading || !postcode.trim()} className="rounded-none" data-testid="planning-appeals-tool-search-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
          </Button>
        </form>
      </div>

      {loading && (
        <div className="flex justify-center py-8" data-testid="planning-appeals-tool-loading">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      {!loading && error && (
        <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md p-4" data-testid="planning-appeals-tool-error">
          {error}
        </div>
      )}
      {!loading && result && (
        <div className="bg-white border border-border p-6 space-y-2" data-testid="planning-appeals-tool-result">
          <p className="text-xs font-semibold text-muted-foreground uppercase">
            {result.total} appeal(s) found {result.outcode ? `in postcode district ${result.outcode}` : `matching "${result.postcode}"`}
          </p>
          {result.cached && (
            <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="planning-appeals-tool-cached-indicator">
              <Clock className="h-3 w-3" /> Cached result from {formatDMY(result.cached_at)}
            </p>
          )}
          {result.appeals.length > 0 ? (
            <div className="space-y-1.5">
              {result.appeals.map((a) => (
                <div
                  key={a.case_number}
                  onClick={() => setSelectedAppeal(a)}
                  className="border rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-muted/30 transition-colors"
                  data-testid={`planning-appeals-tool-result-${a.case_number}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="truncate min-w-0 flex-1 font-medium">{a.site_address}</span>
                    <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${DECISION_STYLE[a.decision] || "bg-muted text-muted-foreground border-border"}`}>
                      {a.decision || "Unknown"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.case_type} - {a.lpa_name}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{formatDMY(a.decision_date)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">No planning appeals found for this search.</p>
          )}
        </div>
      )}

      <PlanningAppealDetailModal
        appeal={selectedAppeal}
        open={!!selectedAppeal}
        onOpenChange={(v) => !v && setSelectedAppeal(null)}
        publicMode={publicMode}
      />
    </div>
  );
}
